import { useMediaQuery } from "@mui/material";
import { FC, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import Button from "../../components/Button";
import ConfirmModal from "../../components/ConfirmModal";
import ConnectedLayout from "../../components/ConnectedLayout";
import Header from "../../components/Header";
import MemberDashboardHeaderActions from "../../components/MemberDashboardHeaderActions";
import Input from "../../components/Input";
import Modal from "../../components/Modal";
import ModalHeader from "../../components/Modal/ModalHeader";

import { AddIcon, PayoutsIcon, HelpIcon } from "../../assets/img/icons";
import Tooltip from "../../components/Tooltip";
import { AdminUserByIdResponse } from "../../types/api/admin/user";

import { AuthContext } from "../../contexts/AuthContext";
import { getAllRoles } from "../../services/api/admin/role";
import { fetchUserForMemberEdit, getUserById } from "../../services/api/admin/user";
import { MemberEditUser } from "../../types/api/admin/user";
import MemberModal from "../Members/MemberModal";
import {
    createFundingStatus,
    getFundingStatusHistory,
} from "../../services/api/fundingStatus";
import { deletePayout, getPayoutsByUser } from "../../services/api/payout";
import { Payout } from "../../types/Payout";
import { getUserName } from "../../utils/string";
import { usePerformanceVisibility } from "../../hooks/usePerformanceVisibility";
import PayoutModal, { PayoutModalMode } from "./PayoutModal";
import PayoutTimeline from "./PayoutTimeline";
import FundingStatusHero from "./FundingStatusHero";
import FundingHistory from "./FundingHistory";

import { getFormattedDateInput } from "../../utils/date";
import {
    FieldErrors,
    REQUIRED_FIELD,
    clearFieldError,
    notifyFieldErrors,
    setFieldErrors,
} from "../../utils/errors";
import styles from "./styles.module.scss";

const Payouts: FC = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const queryParams = new URLSearchParams(location.search);

    const isMobile = useMediaQuery("(max-width: 1050px)");

    const { user, hasPermission, isStreamOn } = useContext(AuthContext)!;

    const isOwnDashboard = !queryParams.has("userId");
    const userId =
        queryParams.has("userId") && hasPermission("admin-payouts", "read")
            ? queryParams.get("userId")
            : user!.id;
    const canEditMemberProfile =
        !isOwnDashboard && hasPermission("admin-members", "update");
    const { hidePerformance, togglePerformance } =
        usePerformanceVisibility(user?.id, isOwnDashboard);

    const canShowFundingHero =
        (isOwnDashboard && hasPermission("funding-status", "read")) ||
        (!isOwnDashboard && hasPermission("admin-funding-status", "read"));

    const canEditFunding =
        (isOwnDashboard && hasPermission("funding-status", "create")) ||
        (!isOwnDashboard && hasPermission("admin-funding-status", "create"));

    const [loading, setLoading] = useState(true);
    const [userDashboard, setUserDashboard] = useState<
        AdminUserByIdResponse | undefined
    >();
    const [memberModalOpen, setMemberModalOpen] = useState(false);
    const [userToEdit, setUserToEdit] = useState<MemberEditUser | null>(null);
    const [memberRoles, setMemberRoles] = useState<
        { id: string; name: string }[]
    >([]);
    const [payoutModalState, setPayoutModalState] = useState<{
        open: boolean;
        mode: PayoutModalMode;
        payout?: Payout;
    }>({
        open: false,
        mode: "add",
        payout: undefined,
    });
    const [payouts, setPayouts] = useState<Payout[]>([]);
    /** Total et moyenne des payouts sur toute la période (pour le hero) */
    const [allTimePayoutSection, setAllTimePayoutSection] = useState<{
        total: number;
        average: number;
    } | null>(null);
    const [allTimePayoutLoading, setAllTimePayoutLoading] = useState(false);

    const [fundingStatusLoading, setFundingStatusLoading] = useState(false);
    const [fundingStatus, setFundingStatus] = useState<{
        isFunded: boolean;
        fundedAccountsCount: number;
        fundedTotalBalance: string;
        asOfDate: string;
    } | null>(null);

    const [isFundingModalOpen, setIsFundingModalOpen] = useState(false);
    const [fundingIsFunded, setFundingIsFunded] = useState<boolean>(true);
    const [fundingAccountsCount, setFundingAccountsCount] =
        useState<string>("");
    const [fundingTotalBalance, setFundingTotalBalance] =
        useState<string>("");
    const [fundingFieldErrors, setFundingFieldErrors] =
        useState<FieldErrors>({});

    /** Par défaut : tous les payouts (période très large) */
    const defaultStartDate = new Date(2000, 0, 1);
    const defaultEndDate = new Date();

    const [startDate] = useState(defaultStartDate);
    const [endDate] = useState(defaultEndDate);

    const [confirmModal, setConfirmModal] = useState<{
        open: boolean;
        payout: Payout | undefined;
        isLoading?: boolean;
    }>({ open: false, payout: undefined, isLoading: false });

    const [fundingHistoryKey, setFundingHistoryKey] = useState(0);
    const [isFundingConfirmOpen, setIsFundingConfirmOpen] = useState(false);

    const handlePayoutModalOpen = (
        mode: PayoutModalMode,
        payout?: Payout | undefined
    ) => {
        setPayoutModalState({ open: true, mode, payout });
    };

    const formatDate = useMemo(
        () =>
            (value: string | null) => {
                if (!value) return "";
                const date = new Date(value);
                return date.toLocaleDateString("fr-FR", {
                    day: "numeric",
                    month: "long",
                    year: "numeric",
                });
            },
        []
    );

    const openFundingModal = () => {
        if (fundingStatus) {
            setFundingIsFunded(fundingStatus.isFunded);
            setFundingAccountsCount(
                fundingStatus.fundedAccountsCount
                    ? String(fundingStatus.fundedAccountsCount)
                    : ""
            );
            setFundingTotalBalance(
                fundingStatus.fundedTotalBalance
                    ? String(fundingStatus.fundedTotalBalance)
                    : ""
            );
        } else {
            setFundingIsFunded(true);
            setFundingAccountsCount("");
            setFundingTotalBalance("");
        }
        setIsFundingModalOpen(true);
    };

    const handleFundingModalClose = () => {
        setIsFundingModalOpen(false);
        setIsFundingConfirmOpen(false);
        setFundingFieldErrors({});
    };

    const handleFundingSaveClick = () => {
        const accounts = fundingIsFunded
            ? Number(fundingAccountsCount || "0")
            : 0;
        const balance = fundingIsFunded
            ? Number(fundingTotalBalance || "0")
            : 0;

        if (fundingIsFunded) {
            const nextErrors = setFieldErrors(
                ["fundedAccountsCount", !fundingAccountsCount && REQUIRED_FIELD],
                ["fundedTotalBalance", !fundingTotalBalance && REQUIRED_FIELD]
            );
            setFundingFieldErrors(nextErrors);
            if (
                notifyFieldErrors(nextErrors, {
                    message:
                        "Veuillez renseigner le nombre de comptes financés et le solde total financé.",
                    type: "error",
                })
            ) {
                return;
            }
            if (accounts < 0 || balance < 0) {
                toast.error(
                    "Les valeurs pour le nombre de comptes et le solde ne peuvent pas être négatives."
                );
                return;
            }
        } else {
            setFundingFieldErrors({});
        }
        setIsFundingConfirmOpen(true);
    };

    const handleFundingSave = async () => {
        const accounts = fundingIsFunded
            ? Number(fundingAccountsCount || "0")
            : 0;
        const balance = fundingIsFunded
            ? Number(fundingTotalBalance || "0")
            : 0;

        try {
            setFundingStatusLoading(true);
            const updated = await createFundingStatus(userId!, {
                isFunded: fundingIsFunded,
                fundedAccountsCount: accounts,
                fundedTotalBalance: balance,
            });
            setFundingStatus(updated ?? null);
            setFundingHistoryKey((k) => k + 1);
            setIsFundingConfirmOpen(false);
            setIsFundingModalOpen(false);
            toast.success("Statut financé mis à jour avec succès.");
        } catch {
            toast.error(
                "Une erreur s'est produite lors de la mise à jour du statut financé."
            );
        } finally {
            setFundingStatusLoading(false);
        }
    };

    useEffect(() => {
        if (!canEditMemberProfile) return;
        getAllRoles().then(setMemberRoles);
    }, [canEditMemberProfile]);

    const handleEditProfile = async () => {
        try {
            const editUser = await fetchUserForMemberEdit(userId!, memberRoles);
            if (!editUser) {
                toast.error(
                    "Profil incomplet (email, rôle ou statut manquant). Vérifiez la réponse GET /v3/admin/users/:id.",
                );
                return;
            }
            setUserToEdit(editUser);
            setMemberModalOpen(true);
        } catch {
            toast.error("Impossible de charger le profil du membre.");
        }
    };

    const refreshUserDashboard = useCallback(() => {
        if (!isOwnDashboard) {
            getUserById(userId!).then((user) => {
                setUserDashboard(user);
            });
        }
    }, [isOwnDashboard, userId]);

    useEffect(() => {
        refreshUserDashboard();
    }, [refreshUserDashboard]);

    const title = isOwnDashboard
        ? "Mes payouts"
        : userDashboard
        ? `Payouts de ${getUserName(
              userDashboard.firstname ?? "",
              userDashboard.lastname ?? "",
              userDashboard.pseudo ?? "",
              isStreamOn
          )}`
        : "Chargement...";

    const headerProps = {
        titleOptionalButtons: isOwnDashboard ? (
            <Button
                size="small"
                variant="primary"
                onClick={togglePerformance}
                label={
                    hidePerformance
                        ? "Afficher les perfs"
                        : "Masquer les perfs"
                }
            />
        ) : (
            userId && (
                <MemberDashboardHeaderActions
                    userId={userId}
                    currentPage="payouts"
                    canEditMemberProfile={canEditMemberProfile}
                    onEditProfile={handleEditProfile}
                />
            )
        ),
    };

    useEffect(() => {
        getPayoutsByUser(
            userId!,
            getFormattedDateInput(startDate),
            getFormattedDateInput(endDate)
        )
            .then((response) => {
                setPayouts(response?.payouts ?? []);
            })
            .finally(() => setLoading(false));
    }, [
        userId,
        startDate,
        endDate,
        payoutModalState.open,
        confirmModal.open,
    ]);

    useEffect(() => {
        if (!canShowFundingHero) return;
        setFundingStatusLoading(true);
        getFundingStatusHistory(userId!, { limit: 1, sort: "asOfDate:desc" })
            .then((status) => setFundingStatus(status?.items?.[0] ?? null))
            .finally(() => setFundingStatusLoading(false));
    }, [userId, canShowFundingHero]);

    /** Ouvrir la modale financement si on arrive depuis la popup "Mettre à jour" (reminder) */
    useEffect(() => {
        if (!canShowFundingHero || !canEditFunding) return;
        if (queryParams.get("openFundingModal") !== "1") return;
        openFundingModal();
        const nextParams = new URLSearchParams(location.search);
        nextParams.delete("openFundingModal");
        const nextSearch = nextParams.toString();
        navigate(
            { pathname: location.pathname, search: nextSearch ? `?${nextSearch}` : "" },
            { replace: true }
        );
    // eslint-disable-next-line react-hooks/exhaustive-deps -- openFundingModal stable enough, avoid re-run on every render
    }, [canShowFundingHero, canEditFunding, location.pathname, location.search, navigate]);

    /** Total des payouts "forever" pour le bandeau hero (indépendant de la période sélectionnée) */
    useEffect(() => {
        if (!canShowFundingHero || !userId) return;
        const farPast = new Date(2000, 0, 1);
        const today = new Date();
        setAllTimePayoutLoading(true);
        getPayoutsByUser(
            userId,
            getFormattedDateInput(farPast),
            getFormattedDateInput(today)
        )
            .then((response) => {
                const section = response?.topSection as
                    | { total: number; average: number }
                    | undefined;
                if (!section) {
                    setAllTimePayoutSection(null);
                    return;
                }
                setAllTimePayoutSection({
                    total: section.total,
                    average: section.average,
                });
            })
            .catch(() => setAllTimePayoutSection(null))
            .finally(() => setAllTimePayoutLoading(false));
    }, [
        userId,
        canShowFundingHero,
        payoutModalState.open,
        confirmModal.open,
    ]);

    // Confirm modal functions
    const handleConfirmOpen = (payout: Payout) => {
        setConfirmModal({ open: true, payout });
    };

    const handleConfirmClose = () => {
        setConfirmModal({ open: false, payout: undefined, isLoading: false });
    };

    const handleConfirm = async () => {
        try {
            setConfirmModal({ ...confirmModal, isLoading: true });
            await deletePayout(confirmModal.payout!.id);
            toast.success(`Payout supprimé avec succès`);
        } catch (error) {
            toast.error(
                "Une erreur s'est produite, veuillez réessayer plus tard"
            );
        } finally {
            handleConfirmClose();
        }
    };

    return (
        <ConnectedLayout>
            <MemberModal
                open={memberModalOpen}
                handleClose={() => {
                    setMemberModalOpen(false);
                    setUserToEdit(null);
                }}
                user={userToEdit ?? undefined}
                roles={memberRoles}
                onUserUpdated={refreshUserDashboard}
            />
            <ConfirmModal
                open={confirmModal.open}
                handleClose={handleConfirmClose}
                handleConfirm={handleConfirm}
                isLoading={confirmModal.isLoading}
            />
            <PayoutModal
                admin={!isOwnDashboard}
                userId={userId!}
                mode={payoutModalState.mode}
                payout={payoutModalState.payout}
                open={payoutModalState.open}
                handleClose={() =>
                    setPayoutModalState({
                        open: false,
                        mode: "add",
                        payout: undefined,
                    })
                }
            />
            <Header icon={PayoutsIcon} title={title} {...headerProps} />
            <div className={styles.container}>
                {canShowFundingHero && (
                    <FundingStatusHero
                        loading={fundingStatusLoading}
                        status={fundingStatus}
                        payoutSection={allTimePayoutSection ?? undefined}
                        payoutsLoading={allTimePayoutLoading}
                        hidePerformance={hidePerformance}
                        canEdit={canEditFunding}
                        onEdit={openFundingModal}
                    />
                )}

                <div className={styles.payoutsRow}>
                    <div className={styles.payoutsHistory}>
                        <PayoutTimeline
                            isLoading={loading}
                            payouts={payouts}
                            hidePerformance={hidePerformance}
                            handlePayoutModalOpen={handlePayoutModalOpen}
                            handleConfirmOpen={handleConfirmOpen}
                            isOwnDashboard={isOwnDashboard}
                            headerAction={
                                ((isOwnDashboard && hasPermission("payout", "create")) ||
                                    (!isOwnDashboard &&
                                        hasPermission("admin-payouts", "create"))) ? (
                                    <Button
                                        label={!isMobile ? "Ajouter un payout" : ""}
                                        onClick={() => handlePayoutModalOpen("add")}
                                        Icon={AddIcon}
                                        size="medium"
                                    />
                                ) : undefined
                            }
                        />
                    </div>
                    {canShowFundingHero && (
                        <div className={styles.fundingHistory}>
                            <FundingHistory
                                key={fundingHistoryKey}
                                userId={userId!}
                                hidePerformance={hidePerformance}
                            />
                        </div>
                    )}
                </div>
            </div>

            <Modal
                open={isFundingModalOpen}
                onClose={handleFundingModalClose}
                size="small"
                canClose={false}
            >
                <ModalHeader
                    title={
                        fundingStatus
                            ? "Mettre à jour mon statut financé"
                            : "Renseigner mon statut financé"
                    }
                    handleClose={handleFundingModalClose}
                    subtitle="Ces informations permettent de suivre votre progression."
                    icon="🏆"
                />
                <div className={styles.fundingModalContent}>
                    {/* Preview card — rappel des informations précédentes (données sauvegardées) */}
                    <div className={styles.fundingModalPreview}>
                        <span className={styles.fundingModalPreviewTitle}>
                            Informations précédentes
                        </span>
                        <div className={styles.fundingModalPreviewGrid}>
                            <div className={styles.fundingModalPreviewItem}>
                                <span className={styles.fundingModalPreviewLabel}>
                                    Statut
                                </span>
                                <span
                                    className={
                                        fundingStatus?.isFunded
                                            ? styles.fundingModalPreviewBadgeFunded
                                            : styles.fundingModalPreviewBadgeNotFunded
                                    }
                                >
                                    {fundingStatus
                                        ? fundingStatus.isFunded
                                            ? "Financé"
                                            : "Non financé"
                                        : "Non renseigné"}
                                </span>
                            </div>
                            <div className={styles.fundingModalPreviewItem}>
                                <span className={styles.fundingModalPreviewLabel}>
                                    Nb de comptes financés
                                </span>
                                <span className={styles.fundingModalPreviewValue}>
                                    {fundingStatus
                                        ? String(
                                              fundingStatus.fundedAccountsCount ?? 0
                                          )
                                        : "—"}
                                </span>
                            </div>
                            <div className={styles.fundingModalPreviewItem}>
                                <span className={styles.fundingModalPreviewLabel}>
                                    Capital total (compte +PnL)
                                </span>
                                <span className={styles.fundingModalPreviewValue}>
                                    {fundingStatus
                                        ? new Intl.NumberFormat("fr-FR", {
                                              maximumFractionDigits: 0,
                                          }).format(
                                              Number(
                                                  fundingStatus.fundedTotalBalance || "0"
                                              )
                                          )
                                        : "—"}
                                </span>
                            </div>
                            {fundingStatus?.asOfDate && (
                                <div
                                    className={
                                        styles.fundingModalPreviewItemFull
                                    }
                                >
                                    <span
                                        className={
                                            styles.fundingModalPreviewLabel
                                        }
                                    >
                                        Dernière mise à jour
                                    </span>
                                    <span
                                        className={
                                            styles.fundingModalPreviewValue
                                        }
                                    >
                                        {formatDate(fundingStatus.asOfDate)}
                                    </span>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Segmented control : Non financé | Financé ✓ */}
                    <div className={styles.fundingModalField}>
                        <span className={styles.fundingModalLabel}>
                            Statut actuel
                        </span>
                        <div className={styles.fundingModalSegment}>
                            <button
                                type="button"
                                className={
                                    !fundingIsFunded
                                        ? styles.fundingModalSegmentActiveNotFunded
                                        : styles.fundingModalSegmentBtn
                                }
                                onClick={() => {
                                    setFundingIsFunded(false);
                                    setFundingFieldErrors({});
                                }}
                            >
                                Non financé
                            </button>
                            <button
                                type="button"
                                className={
                                    fundingIsFunded
                                        ? styles.fundingModalSegmentActiveFunded
                                        : styles.fundingModalSegmentBtn
                                }
                                onClick={() => setFundingIsFunded(true)}
                            >
                                Financé ✓
                            </button>
                        </div>
                    </div>

                    {/* Grille 2 colonnes : visible uniquement si Financé */}
                    {fundingIsFunded && (
                        <div className={styles.fundingModalGrid}>
                            <div className={styles.fundingModalField}>
                                <span className={styles.fundingModalLabel}>
                                    Nb de comptes financés
                                </span>
                                <Input
                                    type="number"
                                    value={fundingAccountsCount}
                                    onChanged={(value) => {
                                        setFundingAccountsCount(value);
                                        setFundingFieldErrors((prev) =>
                                            clearFieldError(
                                                prev,
                                                "fundedAccountsCount"
                                            )
                                        );
                                    }}
                                    min={"0"}
                                    error={
                                        fundingFieldErrors.fundedAccountsCount
                                    }
                                />
                            </div>
                            <div className={styles.fundingModalField}>
                                <span className={styles.fundingModalLabel}>
                                    Capital total (compte +PnL) ($)
                                    <Tooltip
                                        content="Indiquez le montant total de vos comptes.
Pensez à mettre cette information à jour au moins une fois tous les 30 jours afin que nous puissions suivre votre situation correctement.)"
                                        placement="top"
                                    >
                                        <span className={styles.fundingModalLabelInfo} aria-label="Information">
                                            <HelpIcon width={14} height={14} />
                                        </span>
                                    </Tooltip>
                                </span>
                                <div
                                    className={
                                        styles.fundingModalInputWithSuffixInside
                                    }
                                >
                                    <Input
                                        type="number"
                                        value={fundingTotalBalance}
                                        onChanged={(value) => {
                                            setFundingTotalBalance(value);
                                            setFundingFieldErrors((prev) =>
                                                clearFieldError(
                                                    prev,
                                                    "fundedTotalBalance"
                                                )
                                            );
                                        }}
                                        min={"0"}
                                        placeholder="0"
                                        className={
                                            styles.fundingModalInputWithPadding
                                        }
                                        error={
                                            fundingFieldErrors.fundedTotalBalance
                                        }
                                    />
                                    <span
                                        className={
                                            styles.fundingModalInputSuffixInside
                                        }
                                        aria-hidden
                                    >
                                        $
                                    </span>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
                <div className={styles.fundingModalFooter}>
                    <Button
                        size="small"
                        variant="primary"
                        label="Enregistrer"
                        onClick={handleFundingSaveClick}
                        disabled={fundingStatusLoading}
                    />
                </div>
            </Modal>

            {/* Modale de confirmation avec aperçu avant enregistrement */}
            <Modal
                open={isFundingConfirmOpen}
                onClose={() => setIsFundingConfirmOpen(false)}
                size="small"
            >
                <ModalHeader
                    title="Confirmer l'enregistrement"
                    handleClose={() => setIsFundingConfirmOpen(false)}
                    subtitle="Vérifiez les informations ci-dessous."
                    icon="🏆"
                />
                <div className={styles.fundingModalContent}>
                    <div className={styles.fundingConfirmPreview}>
                        <div className={styles.fundingModalPreviewGrid}>
                            <div className={styles.fundingModalPreviewItem}>
                                <span className={styles.fundingModalPreviewLabel}>
                                    Statut
                                </span>
                                <span
                                    className={
                                        fundingIsFunded
                                            ? styles.fundingModalPreviewBadgeFunded
                                            : styles.fundingModalPreviewBadgeNotFunded
                                    }
                                >
                                    {fundingIsFunded ? "Financé" : "Non financé"}
                                </span>
                            </div>
                            {fundingIsFunded && (
                                <>
                                    <div className={styles.fundingModalPreviewItem}>
                                        <span className={styles.fundingModalPreviewLabel}>
                                            Nb de comptes financés
                                        </span>
                                        <span className={styles.fundingModalPreviewValue}>
                                            {fundingAccountsCount || "0"}
                                        </span>
                                    </div>
                                    <div className={styles.fundingModalPreviewItem}>
                                        <span className={styles.fundingModalPreviewLabel}>
                                            Capital total (compte +PnL)
                                        </span>
                                        <span className={styles.fundingModalPreviewValue}>
                                            {new Intl.NumberFormat("fr-FR", {
                                                maximumFractionDigits: 0,
                                            }).format(Number(fundingTotalBalance || "0"))}{" "}
                                            $
                                        </span>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                </div>
                <div className={styles.fundingModalFooter}>
                    <Button
                        size="small"
                        variant="secondary"
                        label="Annuler"
                        onClick={() => setIsFundingConfirmOpen(false)}
                    />
                    <Button
                        size="small"
                        variant="primary"
                        label="Confirmer"
                        onClick={handleFundingSave}
                        isLoading={fundingStatusLoading}
                    />
                </div>
            </Modal>
        </ConnectedLayout>
    );
};

export default Payouts;
